import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Divider,
  Paper,
  Grid,
  Chip,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import SegmentIcon from '@mui/icons-material/AutoFixHigh';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StraightenIcon from '@mui/icons-material/Straighten';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BodyPartIcon from '@mui/icons-material/Accessibility';
import CommentIcon from '@mui/icons-material/Comment';
import SegmentedImage from './SegmentedImage';

interface ImageViewerModalProps {
  open: boolean;
  onClose: () => void;
  imageBase64: string;
  segmentationResult?: any;
  preRenderedBase64?: string;
  title?: string;
  // Patient details
  age?: number;
  bodyPart?: string;
  diameterLargerThanPencil?: boolean;
  geolocationCity?: string;
  geolocationRegion?: string;
  additionalComment?: string;
  timestamp?: string;
}

const ImageViewerModal: React.FC<ImageViewerModalProps> = ({
  open,
  onClose,
  imageBase64,
  segmentationResult,
  preRenderedBase64,
  title = 'Image Viewer',
  age,
  bodyPart,
  diameterLargerThanPencil,
  geolocationCity,
  geolocationRegion,
  additionalComment,
  timestamp,
}) => {
  const [viewMode, setViewMode] = useState<'original' | 'segmented'>('original');

  const handleViewModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: 'original' | 'segmented' | null,
  ) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '70vh',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography variant="h6">{title}</Typography>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <Divider />
      
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {/* Patient Details Section */}
        {(age || bodyPart || geolocationCity || additionalComment || timestamp) && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: 'grey.50',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
              📋 Patient & Image Details
            </Typography>
            <Grid container spacing={1.5}>
              {timestamp && (
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CalendarTodayIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      <strong>Date:</strong> {new Date(timestamp).toLocaleDateString()}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {age && (
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      <strong>Age:</strong> {age} years
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {bodyPart && (
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <BodyPartIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      <strong>Body Part:</strong> {bodyPart}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {diameterLargerThanPencil !== undefined && (
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <StraightenIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      <strong>Diameter {'>'} 6mm:</strong>{' '}
                      <Chip
                        label={diameterLargerThanPencil ? 'Yes' : 'No'}
                        size="small"
                        color={diameterLargerThanPencil ? 'warning' : 'success'}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {(geolocationCity || geolocationRegion) && (
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LocationOnIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      <strong>Location:</strong> {geolocationCity}{geolocationCity && geolocationRegion ? ', ' : ''}{geolocationRegion}
                    </Typography>
                  </Stack>
                </Grid>
              )}
              {additionalComment && (
                <Grid item xs={12}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <CommentIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.3 }} />
                    <Typography variant="body2">
                      <strong>Comments:</strong> {additionalComment}
                    </Typography>
                  </Stack>
                </Grid>
              )}
            </Grid>
          </Paper>
        )}

        {/* Toggle Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            aria-label="image view mode"
            size="small"
          >
            <ToggleButton value="original" aria-label="original image">
              <ImageIcon sx={{ mr: 1 }} />
              Original
            </ToggleButton>
            <ToggleButton value="segmented" aria-label="segmented image">
              <SegmentIcon sx={{ mr: 1 }} />
              With Overlay
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Image Display */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.100',
            borderRadius: 1,
            p: 2,
            minHeight: '400px',
          }}
        >
          {viewMode === 'original' ? (
            <img
              src={`data:image/jpeg;base64,${imageBase64}`}
              alt="Original"
              style={{
                maxWidth: '100%',
                maxHeight: '60vh',
                objectFit: 'contain',
                borderRadius: '4px',
              }}
            />
          ) : (
            <SegmentedImage
              imageBase64={imageBase64}
              segmentationResult={segmentationResult}
              preRenderedBase64={preRenderedBase64}
              lineWidth={5}
              lineColor="#ff0000"
              alt="Segmented"
              style={{
                maxWidth: '100%',
                maxHeight: '60vh',
                objectFit: 'contain',
              }}
            />
          )}
        </Box>

        {/* Info Text */}
        <Typography variant="caption" color="text.secondary" align="center">
          {viewMode === 'original' 
            ? 'Viewing original image without segmentation overlay'
            : 'Viewing image with segmentation overlay (red outline)'}
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default ImageViewerModal;

